/**
 * Activity Feed Widget
 *
 * Compact widget for displaying recent activities on the dashboard.
 * Shows a configurable number of entries and links to the full feed.
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronRight,
  Activity,
  Loader2,
  Baby,
  PartyPopper,
  FileText,
  Milestone,
  CalendarDays,
  Users,
  UserMinus,
  CheckSquare,
  ListChecks,
  Heart,
  Star,
  Calendar,
  CalendarCheck,
  Sparkles,
  Trophy,
  Cake,
  Utensils,
  Droplets,
  Moon,
  Pill,
  Thermometer,
  StickyNote,
  type LucideIcon,
} from 'lucide-react';
import { useActivityFeed } from '../hooks/useActivityFeed';
import { ActivityFeedEntry, ACTIVITY_TYPE_CONFIG, LucideIconName } from '../types';
import { formatActivityDate } from '../services/activityFeedService';
import { isActivityFeedEnabled } from '@/services/systemSettings';

// Map icon names to Lucide components
const ICON_MAP: Record<LucideIconName, LucideIcon> = {
  Baby,
  PartyPopper,
  FileText,
  Milestone,
  CalendarDays,
  Users,
  UserMinus,
  CheckSquare,
  ListChecks,
  Heart,
  Star,
  Calendar,
  CalendarCheck,
  Sparkles,
  Trophy,
  Cake,
  Utensils,
  Droplets,
  Moon,
  Pill,
  Thermometer,
  StickyNote,
};

export function ActivityFeedWidget() {
  const [showWidget, setShowWidget] = useState(false);
  const { activities, isLoading, error } = useActivityFeed({
    dashboardMode: true,
    autoFetch: true,
  });

  useEffect(() => {
    isActivityFeedEnabled().then(setShowWidget);
  }, []);

  // Don't show widget until setting is loaded and confirmed enabled
  if (!showWidget) return null;

  // Don't show widget if loading or no activities
  if (isLoading) {
    return (
      <section className="mt-16">
        <div className="flex items-center gap-3 mb-4">
          <Activity className="w-6 h-6 text-white" />
          <h2 className="text-white text-xl font-medium">Letzte Aktivitäten</h2>
        </div>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-white/50" />
        </div>
      </section>
    );
  }

  if (error || activities.length === 0) {
    return null; // Don't show empty widget
  }

  return (
    <section className="mt-16">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Activity className="w-6 h-6 text-white" />
          <h2 className="text-white text-xl font-medium">Letzte Aktivitäten</h2>
        </div>
        <Link
          to="/activity"
          className="px-3 py-1.5 bg-pp-orange rounded-[5px] text-white text-sm font-medium"
        >
          Alle anzeigen
        </Link>
      </div>

      <div className="space-y-2">
        {activities.map((activity) => (
          <ActivityFeedItem key={activity.id} activity={activity} />
        ))}
      </div>
    </section>
  );
}

interface ActivityFeedItemProps {
  activity: ActivityFeedEntry;
}

function ActivityFeedItem({ activity }: ActivityFeedItemProps) {
  const config = ACTIVITY_TYPE_CONFIG[activity.activity_type] || {
    icon: 'FileText' as LucideIconName,
    color: 'text-white',
    bgColor: 'bg-white/20',
    label: activity.activity_type,
  };

  // Get the icon component from the map
  const IconComponent = ICON_MAP[config.icon] || FileText;

  const formattedDate = formatActivityDate(activity.activity_date);

  // Check if it's an SSW activity with developments (milestone or change)
  const isSSWActivity = activity.activity_type === 'ssw_milestone' || activity.activity_type === 'ssw_change';
  const rawDevelopments = activity.metadata?.developments;

  // Parse developments - can be string[] or JSON array from database
  let developments: string[] = [];
  if (rawDevelopments) {
    if (Array.isArray(rawDevelopments)) {
      developments = rawDevelopments as string[];
    } else if (typeof rawDevelopments === 'string') {
      developments = [rawDevelopments];
    }
  }

  return (
    <div className="glass-effect rounded-[15px] p-4">
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${config.bgColor}`}>
          <IconComponent className={`w-4.5 h-4.5 ${config.color}`} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Date */}
          <p className="text-white/50 text-xs mb-1">{formattedDate}</p>

          {/* Title */}
          <p className="text-white font-medium text-sm">{activity.title}</p>

          {/* Description */}
          {activity.description && (
            <p className="text-white/70 text-xs mt-1">{activity.description}</p>
          )}

          {/* SSW Activity Developments - with ChevronRight icons */}
          {isSSWActivity && developments.length > 0 && (
            <ul className="mt-2 space-y-1">
              {developments.slice(0, 2).map((dev, index) => (
                <li key={index} className="text-white/75 text-xs flex items-start gap-1">
                  <ChevronRight className="w-3 h-3 text-pp-orange flex-shrink-0 mt-0.5" />
                  <span>{dev}</span>
                </li>
              ))}
              {developments.length > 2 && (
                <li className="text-white/50 text-xs pl-4">
                  +{developments.length - 2} weitere...
                </li>
              )}
            </ul>
          )}

          {/* Actor (for partner activities) */}
          {activity.actor_display_name && activity.actor_user_id !== activity.user_id && (
            <p className="text-white/50 text-xs mt-1">
              von {activity.actor_display_name}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default ActivityFeedWidget;
